import { useEffect, useState } from "react";
import axios from "axios";
import { baseUrl } from "../../../baseUrl";
import { useAuth } from "../../../AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../../api";

const GenerateExam = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);

  const [subject, setSubject] = useState("");
  const [topicId, setTopicId] = useState("");
  const [difficulty, setDifficulty] = useState("medium");
  const [numberOfQuestions, setNumberOfQuestions] = useState(20);
  const [timerEnabled, setTimerEnabled] = useState(false);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${baseUrl}/subject/all`)
      .then((res) => setSubjects(res.data))
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => {
    if (!subject) return;

    axios
      .get(`${baseUrl}/topic/by-subject`, {
        params: { subject },
      })
      .then((res) => setTopics(res.data))
      .catch((err) => console.log(err));
  }, [subject]);

  const handleGenerate = async () => {
    if (!topicId) return toast.error("Select topic");
    if (!difficulty) return toast.error("Select difficulty");
    if (!numberOfQuestions) return toast.error("Select number of questions");

    setLoading(true);

    try {
      let timeLimit = null;

      if (timerEnabled) {
        if (difficulty === "hard") {
          timeLimit = numberOfQuestions * 1.5;
        } else {
          timeLimit = numberOfQuestions * 1;
        }
      }


      const res = await api.post(`${baseUrl}/dynamic-exam/generate`, {
        studentId: user.id,
        topicId,
        difficulty,
        numberOfQuestions,
        timeLimit,
      });

      navigate(`/dynamic-exam/${res.data.examId}`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to generate exam");
    } finally {
      setLoading(false);
    }
  };

  const questionOptions = [20, 30];
  const difficultyOptions = ["easy", "medium", "hard"];

  return (
    <div className="container mt-5 mb-5">
      <div className="card shadow-lg border-0 rounded-4">
        <div className="card-body p-4">
          <h4 className="text-center mb-4">Generate Practice Exam</h4>

          {/* SUBJECT + TOPIC */}
          <div className="row mb-4">
            <div className="col-md-6 mb-2 mb-md-0">
              <label className="form-label fw-bold">Subject</label>
              <select
                className="form-select"
                value={subject}
                onChange={(e) => {
                  setSubject(e.target.value);
                  setTopicId("");
                }}
              >
                <option value="">Select Subject</option>
                {subjects.map((s) => (
                  <option key={s._id} value={s.subjectName}>
                    {s.subjectName}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-bold">Topic</label>
              <select
                className="form-select"
                value={topicId}
                onChange={(e) => setTopicId(e.target.value)}
                disabled={!subject}
              >
                <option value="">Select Topic</option>
                {topics.map((t) => (
                  <option key={t._id} value={t._id}>
                    {t.topicName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-4 text-center">
            <label className="form-label fw-bold">Difficulty</label>

            <div className="d-flex justify-content-center gap-3 mt-2">
              {difficultyOptions.map((level) => {
                const isActive = difficulty === level;

                return (
                  <div
                    key={level}
                    onClick={() => setDifficulty(level)}
                    style={{
                      width: "90px",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "10px",
                      cursor: "pointer",
                      fontWeight: "600",
                      fontSize: "14px",
                      textTransform: "uppercase",
                      border: "2px solid",
                      borderColor: isActive ? "#0d6efd" : "#dee2e6",
                      background: isActive
                        ? "linear-gradient(135deg, #0d6efd, #0b5ed7)"
                        : "#f8f9fa",
                      color: isActive ? "#fff" : "#333",
                      boxShadow: isActive
                        ? "0 8px 18px rgba(13,110,253,0.3)"
                        : "0 2px 6px rgba(0,0,0,0.08)",
                      transition: "all 0.25s ease",
                    }}
                  >
                    {level}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-4 text-center">
            <label className="form-label fw-bold">Number of Questions</label>

            <div className="d-flex justify-content-center gap-3 mt-2">
              {questionOptions.map((num) => {
                const isActive = numberOfQuestions === num;

                return (
                  <div
                    key={num}
                    onClick={() => setNumberOfQuestions(num)}
                    style={{
                      width: "40px",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "10px",
                      cursor: "pointer",
                      fontWeight: "600",
                      fontSize: "16px",
                      border: "2px solid",
                      borderColor: isActive ? "#0d6efd" : "#dee2e6",
                      background: isActive
                        ? "linear-gradient(135deg, #0d6efd, #0b5ed7)"
                        : "#f8f9fa",
                      color: isActive ? "#fff" : "#333",
                      boxShadow: isActive
                        ? "0 8px 18px rgba(13,110,253,0.3)"
                        : "0 2px 6px rgba(0,0,0,0.08)",
                      transition: "all 0.25s ease",
                    }}
                  >
                    {num}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-3 d-flex justify-content-between align-items-center">
            <label className="form-label fw-bold mb-0">Enable Timer</label>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                checked={timerEnabled}
                onChange={() => setTimerEnabled(!timerEnabled)}
              />
            </div>
          </div>

          {timerEnabled && (
            <div className="alert alert-info text-center py-2">
              {(() => {
                const totalMinutes =
                  difficulty === "hard"
                    ? numberOfQuestions * 1.5
                    : numberOfQuestions;

                const mins = Math.floor(totalMinutes);
                const secs = Math.round((totalMinutes - mins) * 60);

                return (
                  <div className="alert alert-info text-center py-2">
                    Total Time: {mins} min {secs > 0 && `${secs} sec`}
                  </div>
                );
              })()}

            </div>
          )}

          <div className="text-center mt-4">
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="btn btn-primary px-5 rounded-pill"
            >
              {loading ? "Generating..." : "Start Exam"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateExam;